import React, { useEffect, useState } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, Info } from 'lucide-react';
import { checkAssetDependencies } from '@/lib/mediaLibrary';

/**
 * Safe deletion (section 10) — surfaces whatever real, local dependency
 * information this browser already has (Optimize variants, Schedule
 * posts) before a PERMANENT delete. Clip usage can't be verified without
 * a backend dependency API, so that is named as an explicit unknown
 * rather than silently skipped. For server-backed assets this calls the
 * real `DELETE /api/assets/{id}` — nothing here simulates success.
 */
export function DeleteAssetDialog({ asset, creatorId, open, onOpenChange, onConfirm }) {
  const [deps, setDeps] = useState({ warnings: [], uncheckable: [] });

  useEffect(() => {
    if (open && asset && !asset.localOnly) setDeps(checkAssetDependencies(creatorId, asset.id));
    else setDeps({ warnings: [], uncheckable: [] });
  }, [open, asset, creatorId]);

  if (!asset) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-[#14171F] border-white/10" data-testid="delete-asset-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">Delete "{asset.name}"?</AlertDialogTitle>
          <AlertDialogDescription className="text-[#8A93A6]">
            {asset.localOnly
              ? 'This removes the design from this browser. It was never saved to a server, so this cannot be recovered.'
              : 'This permanently removes the file and its database record from Backblaze B2. This cannot be undone.'}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {deps.warnings.length > 0 && (
          <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3" data-testid="delete-asset-dependency-warnings">
            <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-200 space-y-1">
              <p className="font-medium">This asset is still in use:</p>
              {deps.warnings.map((w, i) => <p key={i}>{w}</p>)}
            </div>
          </div>
        )}
        {deps.uncheckable.length > 0 && (
          <div className="flex items-start gap-2 bg-white/[0.03] border border-white/5 rounded-lg p-3" data-testid="delete-asset-uncheckable-note">
            <Info className="h-4 w-4 text-[#8A93A6] shrink-0 mt-0.5" />
            <div className="text-xs text-[#8A93A6] space-y-1">{deps.uncheckable.map((w, i) => <p key={i}>{w}</p>)}</div>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel className="border-white/10 bg-transparent text-white hover:bg-white/5" data-testid="delete-asset-cancel-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-red-600 hover:bg-red-700" data-testid="delete-asset-confirm-btn">Delete Permanently</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
